import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Youtube, Music2 } from "lucide-react";
import { FadeIn } from "@/components/Reveal";
import { BUYER_FORM_URL, VENDOR_FORM_URL } from "@/constants/links";

const nav = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/marketplace", label: "Marketplace" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms-and-conditions", label: "Terms & Conditions" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-[#1E3A8A] text-white">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16">
        <FadeIn>
          <div className="flex items-center gap-4">
            <img src="/assets/aflimart-logo.png?v=2" alt="Aflimart Digital Market logo" data-testid="footer-brand-logo" className="h-14 w-auto" />
            <p className="eyebrow" data-testid="footer-eyebrow">Aflimart Digital Market</p>
          </div>
          <h2 className="mt-6 max-w-4xl font-sans text-4xl font-bold leading-[1.08] tracking-tight sm:text-6xl lg:text-7xl" data-testid="footer-tagline">
            <span className="text-white">SHOP THE WORLD</span>{" "}
            <span className="text-[#F97316]">FROM HOME.</span>
          </h2>
        </FadeIn>

        <div className="mt-16 grid grid-cols-1 gap-12 border-t border-white/15 pt-12 md:grid-cols-3">
          <FadeIn delay={0.05}>
            <p className="max-w-xs text-sm leading-relaxed text-blue-100/80">
              A borderless marketplace connecting African shoppers to the world, and the world to
              Africa. Aflimart Digital Market is a product of Aflimart Technologies LTD.
            </p>
            <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 font-sans text-[10px] font-semibold tracking-[0.25em] text-white/90" data-testid="footer-prelaunch-badge">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#F97316]" /> CURRENTLY PRE-LAUNCH
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-white">Links</p>
            <ul className="mt-4 grid grid-cols-2 gap-x-8">
              {nav.map((n) => (
                <li key={n.label}>
                  <Link
                    to={n.to}
                    data-testid={`footer-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group inline-flex items-center gap-1 py-2 text-sm text-blue-100/80 transition-colors duration-300 hover:text-white"
                  >
                    {n.label}
                    <ArrowUpRight size={13} className="opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  </Link>
                </li>
              ))}
            </ul>
          </FadeIn>
          <FadeIn delay={0.15}>
            <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-white">Early Access</p>
            <p className="mt-4 text-sm leading-relaxed text-blue-100/80">
              Be first through the doors. Join the buyer waitlist or register your interest as a vendor.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={BUYER_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-cta-buyer" className="btn-orange !px-5 !py-2.5 !text-xs">Buyer Waitlist</a>
              <a href={VENDOR_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-cta-vendor" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-5 py-2.5 text-xs font-semibold tracking-wide text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10">Become a Vendor</a>
            </div>
          </FadeIn>
        </div>

        <div className="mt-16 flex flex-col gap-6 border-t border-white/15 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3" data-testid="footer-social-links">
            {[
              { icon: Instagram, label: "Instagram", href: "https://instagram.com/aflimart_digitalmarket", testId: "footer-social-instagram" },
              { icon: Music2, label: "TikTok", href: "https://tiktok.com/@aflimart.digital", testId: "footer-social-tiktok" },
              { icon: Youtube, label: "YouTube", href: "https://youtube.com/@AflimartDigitalMarket", testId: "footer-social-youtube" },
            ].map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                data-testid={s.testId}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/25 text-blue-100/80 transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-white/60 hover:text-white"
              >
                <s.icon size={15} />
              </a>
            ))}
          </div>
          <p data-testid="footer-copyright" className="text-xs text-blue-100/60">© 2026 Aflimart Technologies LTD. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
